const SLASH_DATE_PATTERN = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})\s*$/

/**
 * Maps 2-digit years using the industry-standard pivot:
 *   00-69 -> 2000-2069
 *   70-99 -> 1970-1999
 */
function expandTwoDigitYear(year: number): number {
  return year <= 69 ? 2000 + year : 1900 + year
}

function pad2(value: number): string {
  return value.toString().padStart(2, "0")
}

/** Format an ISO date string (YYYY-MM-DD) for display as MM/DD/YYYY, or return input unchanged if malformed. */
export function formatIsoForDisplay(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!match) return iso
  const [, year, month, day] = match
  return `${month}/${day}/${year}`
}

/**
 * Parse a shorthand date string into an ISO 8601 (YYYY-MM-DD) string.
 * Accepts MM/DD/YY, M/D/YY, MM/DD/YYYY, M/D/YYYY. Slashes only (never dashes —
 * avoids ambiguity with ISO YYYY-MM-DD input).
 *
 * Returns null if input is unparseable or represents an impossible calendar
 * date (e.g. 2/30/25). Uses explicit (year, month, day) component construction
 * via Date.UTC and verifies round-trip equality. Never uses `new Date(string)`
 * (locale/timezone footguns).
 */
export function parseDateInput(raw: string): string | null {
  if (raw.trim() === "") return null
  const match = SLASH_DATE_PATTERN.exec(raw)
  if (!match) return null

  const monthNum = Number(match[1])
  const dayNum = Number(match[2])
  const yearRaw = match[3]
  const yearNum =
    yearRaw.length === 2 ? expandTwoDigitYear(Number(yearRaw)) : Number(yearRaw)

  if (!Number.isInteger(monthNum) || monthNum < 1 || monthNum > 12) return null
  if (!Number.isInteger(dayNum) || dayNum < 1 || dayNum > 31) return null
  if (!Number.isInteger(yearNum) || yearNum < 1 || yearNum > 9999) return null

  // Use Date.UTC to avoid DST/locale offsets; verify components match to catch
  // impossible dates like Feb 30 (which would roll forward to March 2).
  const utcMs = Date.UTC(yearNum, monthNum - 1, dayNum)
  const verify = new Date(utcMs)
  if (
    verify.getUTCFullYear() !== yearNum ||
    verify.getUTCMonth() !== monthNum - 1 ||
    verify.getUTCDate() !== dayNum
  ) {
    return null
  }

  return `${yearNum.toString().padStart(4, "0")}-${pad2(monthNum)}-${pad2(dayNum)}`
}
